import React, { useEffect, useState } from 'react';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';

import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Col, Flex, Row, Tooltip, Typography } from 'antd';

import { InfoCircleTwoTone, MenuOutlined } from '@ant-design/icons';
import { SPARQLE_DARK_BLUE } from '../../constants';

const commonStyle = {
  cursor: 'grab',
  transition: 'unset', // Prevent element from shaking after drag
};

const DraggableTag = ({ item }) => {
  const { Paragraph } = Typography;
  const { listeners, transform, transition, isDragging, setNodeRef } = useSortable({ id: item.id });

  const style = transform
    ? {
        ...commonStyle,
        cursor: 'grabbing',
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        transition: isDragging ? 'unset' : transition, // Improve performance/visual effect when dragging
      }
    : commonStyle;

  return (
    <Row
      style={{
        alignItems: 'center',
        border: '1px solid lightGray',
        borderRadius: '5px',
        paddingLeft: 12,
        backgroundColor: 'white',
        ...style,
      }}
      ref={setNodeRef}
      {...listeners}
    >
      <Col span={10}>
        <strong>{item.name}</strong>
      </Col>
      <Col span={11}>
        <Paragraph ellipsis={{ lines: 1 }} style={{ paddingRight: 8, marginBottom: 8, marginTop: 8 }}>
          {item.type}
        </Paragraph>
      </Col>
      <Col span={1}>
        <Tooltip title={item.description} placement="left">
          <InfoCircleTwoTone twoToneColor={`#${SPARQLE_DARK_BLUE}`} style={{ cursor: 'default' }} />
        </Tooltip>
      </Col>
      <Col span={2}>
        <MenuOutlined style={{ color: 'lightGray' }} />
      </Col>
    </Row>
  );
};

export const DeliveryAreaPrio = ({ deliveryAreas, setPrio }) => {
  const [items, setItems] = useState(
    deliveryAreas.map((d) => ({
      id: d.id,
      name: d.enhancedName,
      type: d.type === 'POSTAL_CODES' ? `Postal codes` : 'Polygon',
      description:
        d.postalCodes
          ?.slice(0, 30)
          ?.join(', ')
          .concat(d.postalCodes.length > 30 ? '...' : '') || '',
    })),
  );

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) {
      return;
    }
    if (active.id !== over.id) {
      setItems((data) => {
        const oldIndex = data.findIndex((item) => item.id === active.id);
        const newIndex = data.findIndex((item) => item.id === over.id);
        return arrayMove(data, oldIndex, newIndex);
      });
    }
  };

  useEffect(() => {
    setPrio(items); // pass it up
  }, [items]);

  return (
    <div>
      <DndContext sensors={sensors} onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <Flex vertical gap="8px" wrap="wrap">
            {items.map((item) => (
              <DraggableTag item={item} key={item.id} />
            ))}
          </Flex>
        </SortableContext>
      </DndContext>
    </div>
  );
};
